import { __awaiter, __generator, __read } from "tslib";
import React from 'react';
import PropagateLoader from 'react-spinners/PropagateLoader';
import { Modal } from '../Modal';
import { formatZipErrorMessage, isZipJobCancelled, triggerFileDownload, } from '../utils/asyncZipJob';
var DEFAULT_CLOSE_COUNTDOWN_SECONDS = 5;
export var AsyncZipDownloadModal = function (props) {
    var show = props.show, onHide = props.onHide, scopeLabel = props.scopeLabel, prepareZip = props.prepareZip, _a = props.closeCountdownSeconds, closeCountdownSeconds = _a === void 0 ? DEFAULT_CLOSE_COUNTDOWN_SECONDS : _a, _b = props.hideCloseWhilePreparing, hideCloseWhilePreparing = _b === void 0 ? true : _b, onCancelJob = props.onCancelJob, onDownloadReady = props.onDownloadReady;
    var _c = __read(React.useState('preparing'), 2), status = _c[0], setStatus = _c[1];
    var _d = __read(React.useState(''), 2), progressMessage = _d[0], setProgressMessage = _d[1];
    var _e = __read(React.useState(''), 2), errorMessage = _e[0], setErrorMessage = _e[1];
    var _f = __read(React.useState(''), 2), warningMessage = _f[0], setWarningMessage = _f[1];
    var _g = __read(React.useState(''), 2), downloadUrl = _g[0], setDownloadUrl = _g[1];
    var _h = __read(React.useState(closeCountdownSeconds), 2), closeCountdown = _h[0], setCloseCountdown = _h[1];
    var runIdRef = React.useRef(0);
    var cancelledRef = React.useRef(false);
    var activeJobIdRef = React.useRef(null);
    React.useEffect(function () {
        if (!show) {
            setStatus('preparing');
            setProgressMessage('');
            setErrorMessage('');
            setWarningMessage('');
            setDownloadUrl('');
            setCloseCountdown(closeCountdownSeconds);
            cancelledRef.current = false;
            activeJobIdRef.current = null;
            return undefined;
        }
        var runId = ++runIdRef.current;
        cancelledRef.current = false;
        activeJobIdRef.current = null;
        var run = function () { return __awaiter(void 0, void 0, void 0, function () {
            var pollOptions, data, err_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setStatus('preparing');
                        setProgressMessage('Preparing your download…');
                        setErrorMessage('');
                        setWarningMessage('');
                        setDownloadUrl('');
                        pollOptions = {
                            isCancelled: function () { return cancelledRef.current || runId !== runIdRef.current; },
                            onJobStarted: function (jobId) {
                                activeJobIdRef.current = jobId;
                            },
                        };
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, prepareZip(function (msg) {
                                if (!cancelledRef.current && runId === runIdRef.current) {
                                    setProgressMessage(msg);
                                }
                            }, pollOptions)];
                    case 2:
                        data = _a.sent();
                        if (cancelledRef.current || runId !== runIdRef.current) {
                            return [2 /*return*/];
                        }
                        if (!data || !data.downloadUrl) {
                            setStatus('error');
                            setErrorMessage('Download could not be prepared.');
                            return [2 /*return*/];
                        }
                        if (data.failed && data.failed > 0) {
                            setWarningMessage('Download is ready, but some variants could not be included.');
                        }
                        setDownloadUrl(data.downloadUrl);
                        setStatus('starting');
                        triggerFileDownload(data.downloadUrl);
                        if (onDownloadReady) {
                            onDownloadReady();
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        err_1 = _a.sent();
                        if (cancelledRef.current || runId !== runIdRef.current || isZipJobCancelled(err_1)) {
                            return [2 /*return*/];
                        }
                        setStatus('error');
                        setErrorMessage(formatZipErrorMessage(err_1));
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        }); };
        run();
        return function () {
            cancelledRef.current = true;
        };
    }, [show, prepareZip, closeCountdownSeconds]);
    React.useEffect(function () {
        if (!show || status !== 'starting') {
            return undefined;
        }
        setCloseCountdown(closeCountdownSeconds);
        var remaining = closeCountdownSeconds;
        var timerId = window.setInterval(function () {
            remaining -= 1;
            if (remaining <= 0) {
                window.clearInterval(timerId);
                onHide();
                return;
            }
            setCloseCountdown(remaining);
        }, 1000);
        return function () {
            window.clearInterval(timerId);
        };
    }, [show, status, onHide, closeCountdownSeconds]);
    var onManualDownloadClick = function (event) {
        event.preventDefault();
        if (downloadUrl) {
            triggerFileDownload(downloadUrl);
        }
    };
    var onCancelClick = function () { return __awaiter(void 0, void 0, void 0, function () {
        var jobId, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    cancelledRef.current = true;
                    runIdRef.current += 1;
                    jobId = activeJobIdRef.current;
                    if (!(jobId && onCancelJob)) return [3 /*break*/, 4];
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, onCancelJob(jobId)];
                case 2:
                    _b.sent();
                    return [3 /*break*/, 4];
                case 3:
                    _a = _b.sent();
                    return [3 /*break*/, 4];
                case 4:
                    onHide();
                    return [2 /*return*/];
            }
        });
    }); };
    var renderBody = function () {
        if (status === 'preparing') {
            return (React.createElement("div", { className: "async-zip-download-modal" },
                React.createElement("p", { className: "async-zip-download-modal__message" }, progressMessage || 'Preparing your download…'),
                scopeLabel ? (React.createElement("p", { className: "async-zip-download-modal__scope" }, scopeLabel)) : null,
                React.createElement("div", { className: "async-zip-download-modal__spinner" },
                    React.createElement(PropagateLoader, null)),
                React.createElement("button", { type: "button", className: "async-zip-download-modal__cancel button", onClick: onCancelClick }, "Cancel")));
        }
        if (status === 'starting') {
            return (React.createElement("div", { className: "async-zip-download-modal" },
                React.createElement("p", { className: "async-zip-download-modal__message" }, "Starting download\u2026"),
                warningMessage ? (React.createElement("p", { className: "async-zip-download-modal__warning" }, warningMessage)) : null,
                downloadUrl ? (React.createElement("p", { className: "async-zip-download-modal__fallback" },
                    React.createElement("a", { href: downloadUrl, className: "async-zip-download-modal__fallback-link", onClick: onManualDownloadClick }, "Click here if download does not start"))) : null,
                React.createElement("p", { className: "async-zip-download-modal__countdown" },
                    "Closing in ",
                    closeCountdown)));
        }
        return (React.createElement("div", { className: "async-zip-download-modal async-zip-download-modal--error" },
            React.createElement("p", { className: "async-zip-download-modal__message" }, errorMessage),
            React.createElement("button", { type: "button", className: "button", onClick: onHide }, "Close")));
    };
    return (React.createElement(Modal, { isShowing: show, hide: status === 'preparing' && hideCloseWhilePreparing ? undefined : onHide, size: "sm", bodyClasses: "confirm-modal async-zip-download-modal-body", hideFooter: true, hideHeader: true, hideClose: status === 'preparing' && hideCloseWhilePreparing }, renderBody()));
};
//# sourceMappingURL=AsyncZipDownloadModal.js.map